import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { evaluateExpressionWithValueGetter } from 'shared-libs/helpers/evaluation'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import {
  IUI2FormContext,
  UI2FormContext,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-form-context'
import { UI2FormProps } from 'shared-libs/components/ui2/forms/ui2-form/ui2-form-props'
import {
  IUI2FormSectionErrorContext,
  UI2FormSectionErrorContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-error-context'
import {
  IUI2FormSectionFillContext,
  UI2FormSectionFillContext,
} from 'shared-libs/components/ui2/forms/ui2-form-section/ui2-form-section-fill-context'
import {
  IUI2WizardContext,
  UI2WizardContext,
  WizardProgressStep,
} from 'shared-libs/components/ui2/forms/ui2-form/ui2-wizard-context'
import {
  advance,
  back,
  derivedStatus,
  deriveSectionStatuses,
  initialWizardState,
  jumpTo,
  revealSection,
  seedQueue,
  skip,
  willEmptyQueueOnAdvance,
  WizardSectionRegistration,
  WizardState,
  WizardStatus,
} from 'shared-libs/components/ui2/forms/ui2-form/wizard-controller'
import { UI2Banner } from '../../display/ui2-banner/ui2-banner'

import './_ui2-form.scss'

/** Stable identity so the wizard context value doesn't churn in default mode. */
const EMPTY_SECTION_STATUSES = {}

const UI2FormContextProvider = UI2FormContext.Provider as React.ComponentType<{
  value: IUI2FormContext
  children?: React.ReactNode
}>
const UI2WizardContextProvider = UI2WizardContext.Provider as React.ComponentType<{
  value: IUI2WizardContext
  children?: React.ReactNode
}>

/**
 * @uiComponent
 */
interface IUI2FormProps extends UI2FormProps, Omit<IBaseProps, 'children'> {
  frames?: FramesManager
  children?: React.ReactNode
}

export const UI2Form = React.forwardRef<HTMLDivElement, IUI2FormProps>((props, ref) => {
  const {
    className,
    style,
    mode = 'default',
    formDensity,
    isReadOnly,
    isDisabled,
    isRequired,
    isOptional,
    isHorizontalLayout,
    isCollapsed,
    isCollapsable,
    enablePrefilledMode,
    frames,
    value,
    onChange,
    successSection,
    autoAdvancePrefilledSections = true,
    children,
    showProgressIndicator,
  } = props

  const isWizard = mode === 'wizard' || mode === 'stacked'
  const resolvedFormDensity = formDensity ? formDensity : isWizard ? 'compact' : 'default'
  const replayEnabled = isWizard && autoAdvancePrefilledSections
  const registrationsRef = useRef<Map<string, WizardSectionRegistration>>(new Map())
  const nextIndexRef = useRef(0)
  const seededRef = useRef(false)
  const [state, setState] = useState<WizardState>(initialWizardState())
  const [isReplaying, setIsReplaying] = useState(false)
  // Captured at seed time. When the form was already 'complete' on load we
  // assume the user has previously reviewed optional fields/steps, so replay
  // gates only on error state. On any other initial status, unfilled bound
  // fields (required or optional) also gate replay so an externally-prefilled
  // form still lands the user on steps they haven't reviewed.
  const replayGatesOnUnfilledRef = useRef(true)
  // Form-level error aggregation for default-mode status. Sections report their
  // aggregated error state up through UI2FormSectionErrorContext.
  const erroredSectionIdsRef = useRef<Set<string>>(new Set())
  const [formErrorCount, setFormErrorCount] = useState(0)
  const [hasSeenClearedError, setHasSeenClearedError] = useState(false)
  // Parallel to erroredSectionIdsRef: which sections have any unfilled child
  // reported through UI2FormSectionFillContext. Feeds the replay gate only.
  const unfilledSectionIdsRef = useRef<Set<string>>(new Set())
  const [unfilledCount, setUnfilledCount] = useState(0)

  // Stable reporter identities: any dependency here would change the context
  // value on every reported error, causing every field's error-reporter
  // cleanup effect to re-run, remove itself from the set, and toggle
  // formErrorCount back — an infinite loop. Read live state via refs and
  // functional setState instead.
  const hasSeenClearedErrorRef = useRef(false)
  hasSeenClearedErrorRef.current = hasSeenClearedError
  const formErrorReporter: IUI2FormSectionErrorContext = useMemo(
    () => ({
      reportError: (id, hasError) => {
        const set = erroredSectionIdsRef.current
        const wasPresent = set.has(id)
        if (hasError) set.add(id)
        else set.delete(id)
        if (wasPresent && !hasError && !hasSeenClearedErrorRef.current) {
          setHasSeenClearedError(true)
        }
        setFormErrorCount((c) => (c === set.size ? c : set.size))
      },
    }),
    []
  )

  const formFillReporter: IUI2FormSectionFillContext = useMemo(
    () => ({
      reportUnfilled: (id, isUnfilled) => {
        const set = unfilledSectionIdsRef.current
        if (isUnfilled) set.add(id)
        else set.delete(id)
        setUnfilledCount((c) => (c === set.size ? c : set.size))
      },
    }),
    []
  )
  // Bump on register/deregister so progressSteps re-derive.
  const [registrationVersion, setRegistrationVersion] = useState(0)

  const evalPredicate = useCallback(
    (expr: string) => {
      if (!expr) return true
      if (!frames) return true
      try {
        return !!evaluateExpressionWithValueGetter(expr, (k: string) => frames.getValue(k))
      } catch {
        return true
      }
    },
    [frames]
  )

  const seedIfReady = useCallback(() => {
    if (seededRef.current) return
    const regs = Array.from(registrationsRef.current.values())
    if (regs.length === 0) return
    seededRef.current = true
    replayGatesOnUnfilledRef.current = value !== 'complete'
    setState(seedQueue(regs, evalPredicate))
    if (replayEnabled) setIsReplaying(true)
  }, [evalPredicate, replayEnabled, value])

  const register = useCallback((reg: Omit<WizardSectionRegistration, 'index'>) => {
    const index = nextIndexRef.current++
    registrationsRef.current.set(reg.id, { ...reg, index })
    setRegistrationVersion((v) => v + 1)
  }, [])
  const deregister = useCallback((id: string) => {
    registrationsRef.current.delete(id)
    setRegistrationVersion((v) => v + 1)
  }, [])

  useEffect(() => {
    if (!isWizard) return
    const t = setTimeout(seedIfReady, 0)
    return () => clearTimeout(t)
  }, [isWizard, seedIfReady])

  // Silent replay: while active, wait a tick for descendant fields to settle
  // their reported error state, then either advance the current section (if
  // clean) or stop replay so the user can act on the section. Each advance
  // triggers this effect again for the new active section.
  useEffect(() => {
    if (!isReplaying) return
    if (!state.activeId) {
      setIsReplaying(false)
      return
    }
    const activeId = state.activeId
    const t = setTimeout(() => {
      const blockedByError = erroredSectionIdsRef.current.has(activeId)
      const blockedByUnfilled =
        replayGatesOnUnfilledRef.current && unfilledSectionIdsRef.current.has(activeId)
      if (blockedByError || blockedByUnfilled) {
        setIsReplaying(false)
        return
      }
      setState((s) =>
        s.activeId === activeId ? advance(s, activeId, registrationsRef.current, evalPredicate) : s
      )
    }, 50)
    return () => clearTimeout(t)
  }, [isReplaying, state.activeId, formErrorCount, unfilledCount, evalPredicate])

  const doAdvance = useCallback(
    (sectionId: string) => {
      setState((s) =>
        s.activeId === sectionId
          ? advance(s, sectionId, registrationsRef.current, evalPredicate)
          : s
      )
    },
    [evalPredicate]
  )
  const doSkip = useCallback(
    (sectionId: string) => {
      setState((s) =>
        s.activeId === sectionId ? skip(s, sectionId, registrationsRef.current, evalPredicate) : s
      )
    },
    [evalPredicate]
  )
  const doBack = useCallback(() => setState((s) => back(s)), [])
  const doJumpTo = useCallback((sectionId: string) => {
    setState((s) => jumpTo(s, sectionId, registrationsRef.current))
  }, [])
  const doRevealSection = useCallback((sectionId: string) => {
    setState((s) => revealSection(s, sectionId, registrationsRef.current))
  }, [])

  const status: WizardStatus = isWizard
    ? derivedStatus(state)
    : formErrorCount === 0
      ? 'complete'
      : hasSeenClearedError
        ? 'inProgress'
        : 'notStarted'
  const lastEmittedRef = useRef<WizardStatus | undefined>(undefined)
  useEffect(() => {
    if (!onChange) return
    if (lastEmittedRef.current === status) return
    if (value === status) {
      lastEmittedRef.current = status
      return
    }
    lastEmittedRef.current = status
    onChange(status)
  }, [status, value, onChange])

  // Computed in-line rather than memoized: entryPredicate evaluates against
  // live field values reached through `frames`, and the frames reference is
  // stable across value changes — memoization would hold stale predicate
  // results. Steps count is small; rebuilding every render is cheap.
  void registrationVersion
  const completedSet = new Set(state.completedStack)
  const visitedSet = new Set(state.visitedIds)
  const everCompletedSet = new Set(state.everCompletedIds)
  const progressSteps: WizardProgressStep[] = !isWizard
    ? []
    : Array.from(registrationsRef.current.values())
        .sort((a, b) => a.index - b.index)
        // Hide a section from the progress indicator until its entryPredicate
        // is truthy — the user shouldn't see steps that aren't yet relevant.
        // Completed and active sections stay visible regardless of predicate.
        .filter(
          (r) =>
            r.entryPredicate == null ||
            evalPredicate(r.entryPredicate) ||
            completedSet.has(r.id) ||
            r.id === state.activeId
        )
        .map((r) => ({
          id: r.id,
          title: r.title,
          isActive: r.id === state.activeId,
          isCompleted: completedSet.has(r.id),
          isVisited: visitedSet.has(r.id),
          wasEverCompleted: everCompletedSet.has(r.id),
        }))

  // Same rationale as progressSteps: predicates read live values, so this is
  // recomputed rather than memoized.
  const sectionStatuses = isWizard
    ? deriveSectionStatuses(state, registrationsRef.current, evalPredicate)
    : EMPTY_SECTION_STATUSES

  const isFinalStep =
    isWizard && willEmptyQueueOnAdvance(state, registrationsRef.current, evalPredicate)
  const isSuccessState = isWizard && state.activeId === null && state.completedStack.length > 0

  const wizardCtx: IUI2WizardContext = useMemo(
    () => ({
      isWizard,
      mode: mode === 'stacked' ? 'stacked' : 'wizard',
      activeSectionId: state.activeId,
      register,
      deregister,
      advance: doAdvance,
      skip: doSkip,
      canGoBack: state.completedStack.length > 0,
      back: doBack,
      jumpTo: doJumpTo,
      revealSection: doRevealSection,
      progressSteps,
      sectionStatuses,
      isFinalStep,
      isSuccessState,
      isReplaying,
    }),
    [
      isWizard,
      mode,
      state.activeId,
      state.completedStack.length,
      register,
      deregister,
      doAdvance,
      doSkip,
      doBack,
      doJumpTo,
      doRevealSection,
      progressSteps,
      sectionStatuses,
      isFinalStep,
      isSuccessState,
      isReplaying,
    ]
  )

  const formCtx: IUI2FormContext = {
    formDensity: resolvedFormDensity,
    isReadOnly,
    isDisabled,
    isRequired,
    isOptional,
    isHorizontalLayout,
    enablePrefilledMode,
    isCollapsed,
    isCollapsable,
  }

  return (
    <UI2FormContextProvider value={formCtx}>
      <UI2WizardContextProvider value={wizardCtx}>
        <UI2FormSectionErrorContext.Provider value={formErrorReporter}>
          <UI2FormSectionFillContext.Provider value={formFillReporter}>
            <div
              ref={ref}
              className={classNames('ui2-form', { 'ui2-form--wizard': isWizard }, className)}
              style={style}
            >
              {mode === 'wizard' &&
                showProgressIndicator &&
                renderWizardHeader(progressSteps, isSuccessState, doJumpTo)}
              {children}
              {isSuccessState && renderSuccessBlock(successSection, progressSteps, doJumpTo)}
            </div>
          </UI2FormSectionFillContext.Provider>
        </UI2FormSectionErrorContext.Provider>
      </UI2WizardContextProvider>
    </UI2FormContextProvider>
  )
})

UI2Form.displayName = 'UI2Form'

// The form's `value` binds its own wizard-status field, not a container path
// for descendant fields. Descendants' `value` bindings resolve from the
// entity root — otherwise every child field would be written into a
// sub-property of the status field and the status write would clobber them.
;(UI2Form as any).isolatesChildValueScope = true

function renderSuccessBlock(
  successSection: React.ReactNode,
  progressSteps: WizardProgressStep[],
  onJumpTo: (id: string) => void
) {
  const firstStepId = progressSteps[0]?.id
  return (
    <div className="ui2-form-successBlock">
      {successSection ?? (
        <UI2Banner mode="success" title="You're all set" markdownText="All steps are complete." />
      )}
      {firstStepId && (
        <div className="ui2-form-successBlock__footer">
          <button
            type="button"
            className="ui2-form-sectionFooter__back"
            onClick={() => onJumpTo(firstStepId)}
          >
            Return to start?
          </button>
        </div>
      )}
    </div>
  )
}

function renderWizardHeader(
  steps: WizardProgressStep[],
  isSuccessState: boolean,
  onJumpTo: (id: string) => void
) {
  return (
    <div className="ui2-form-wizardHeader">
      <ol className="ui2-form-wizardProgress">
        {steps.map((step, idx) => {
          // Only allow jumping BACK to completed sections. A skipped or
          // otherwise-visited-but-uncompleted section is not tappable — the
          // user must reach it again through the normal wizard flow.
          const tappable = step.isCompleted && !step.isActive
          return (
            <li
              key={step.id}
              className={classNames('ui2-form-wizardProgress__step', {
                'is-active': step.isActive,
                'is-completed': step.isCompleted,
                'is-visited': step.isVisited,
                'is-tappable': tappable,
              })}
            >
              <button
                type="button"
                className="ui2-form-wizardProgress__dot"
                onClick={() => tappable && onJumpTo(step.id)}
                disabled={!tappable}
                aria-current={step.isActive ? 'step' : undefined}
                aria-label={step.title ? `${step.title}` : `Step ${idx + 1}`}
              >
                {idx + 1}
              </button>
              {step.title && <span className="ui2-form-wizardProgress__label">{step.title}</span>}
            </li>
          )
        })}
        <li
          className={classNames(
            'ui2-form-wizardProgress__step',
            'ui2-form-wizardProgress__step--done',
            {
              'is-active': isSuccessState,
            }
          )}
        >
          <span className="ui2-form-wizardProgress__dot" aria-label="Complete">
            ✓
          </span>
        </li>
      </ol>
    </div>
  )
}
